import { classInstance } from '@/shared/helpers/helpers';
import { vars } from '@/app/config/variables';

export class ShowMore {
	readonly $container: HTMLElement;
	private $items: HTMLElement[];
	private $button: HTMLButtonElement | null;

	private total: number;
	private shown: number;
	private load: number;
	private itemClass: string;

	private shownTablet: number | null = null;
	private shownDesktop: number | null = null;

	private tabletMQ: MediaQueryList;
	private desktopMQ: MediaQueryList;

	private readonly allItemsShown: string = 'data-show-more-completed';

	constructor(selector: HTMLElement) {
		this.$container = selector;

		if (!this.$container) return;

		this.total = parseInt(this.$container.dataset.showMoreTotal || '0', 10);
		this.shown = parseInt(this.$container.dataset.showMoreShown || '0', 10);
		this.shownTablet = this.$container.dataset.showMoreShownTablet ? parseInt(this.$container.dataset.showMoreShownTablet, 10) : null;
		this.shownDesktop = this.$container.dataset.showMoreShownDesktop ? parseInt(this.$container.dataset.showMoreShownDesktop, 10) : null;

		this.itemClass = this.$container.dataset.showMoreItem || '';

		this.$items = Array.from(this.$container.querySelectorAll<HTMLElement>(`.${this.itemClass}`));
		this.$button = this.$container.querySelector<HTMLButtonElement>('.js-show-more__button');

		this.tabletMQ = window.matchMedia(`(min-width: ${vars.breakpoint.tabletSmall}px)`);
		this.desktopMQ = window.matchMedia(`(min-width: ${vars.breakpoint.laptop}px)`);

		this.init();
	}

	public init(): void {
		classInstance.set(this.$container, { showmore: this });

		this.applyResponsiveShown();

		this.load = this.$container.dataset.showMoreLoad ? parseInt(this.$container.dataset.showMoreLoad, 10) : this.shown;

		this.updateVisibility();

		if (this.$button) {
			this.clickHandler = this.clickHandler.bind(this);
			this.$button.addEventListener('click', this.clickHandler);
		}

		this.tabletMQ.addEventListener('change', this.onMediaChange);
		this.desktopMQ.addEventListener('change', this.onMediaChange);
	}

	public destroy(): void {
		classInstance.del(this.$container, 'showmore');

		if (this.$button) {
			this.$button.removeEventListener('click', this.clickHandler);
		}

		this.tabletMQ.removeEventListener('change', this.onMediaChange);
		this.desktopMQ.removeEventListener('change', this.onMediaChange);
	}

	public reinit(): void {
		this.destroy();
		this.init();
	}

	private applyResponsiveShown(): void {
		if (this.$container.hasAttribute(this.allItemsShown)) return;

		if (this.shownDesktop !== null && this.desktopMQ.matches) {
			this.shown = this.shownDesktop;
		} else if (this.shownTablet !== null && this.tabletMQ.matches) {
			this.shown = this.shownTablet;
		}

		if (this.shown >= this.total) {
			this.shown = this.total;
			this.$container.dataset.showMoreShown = String(this.shown);
			this.$container.setAttribute(this.allItemsShown, '');
			return;
		}

		this.$container.dataset.showMoreShown = String(this.shown);
	}

	private onMediaChange = (): void => {
		this.applyResponsiveShown();

		this.load = this.$container.dataset.showMoreLoad ? parseInt(this.$container.dataset.showMoreLoad, 10) : this.shown;

		this.updateVisibility();
	};

	private updateVisibility(): void {
		this.$items.forEach((item, index) => {
			if (index < this.shown) {
				item.classList.remove('hidden');
			} else {
				item.classList.add('hidden');
			}
		});
	}

	private clickHandler(event: MouseEvent): void {
		event.preventDefault();

		if (this.$container.hasAttribute(this.allItemsShown)) return;

		this.shown += this.load;

		if (this.shown >= this.total) {
			this.shown = this.total;
			this.$container.setAttribute(this.allItemsShown, '');
		}

		this.$container.dataset.showMoreShown = String(this.shown);

		this.updateVisibility();
	}
}
